import { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { fetchAuthSession, JWT } from "aws-amplify/auth";
import GeneralContext from "./GeneralContext";
import { GroupInterface, GroupOfUserInterface, SchoolInterface, UserInterface } from "../../Interfaces/Entities";
import { AuthStatus, ModalTypes } from "../../Interfaces/ContextInterfaces/GeneralContext";
import { Delay } from "../../Helpers/AsyncHelpers";
import { GetSignedInUsername } from "../../Helpers/AuthHelpers";
import { GetDayFromNow } from "../../Helpers/DateHelpers";
import { FetchGetFunction, FetchOptions, FetchPutFunction, IsConnectionProblem, LOGIN_TIMEOUT_MS } from "../../Helpers/FetchHelpers";
import { OneSignalInit, PushNotificationStatus } from "../../Helpers/OneSignalHelpers";
import { GetRedirectURL } from "../../Helpers/TranslateHelpers";
import { GetHomeSection, GetSectionPath, ParseAppPath } from "../../Helpers/RouteHelpers";
import { useLocation, useNavigate } from "react-router-dom";


// Restoring a session retries by itself so the user sees a reconnecting notice instead of
// an error the moment the connection hiccups. Each attempt is short on purpose.
const SESSION_RESTORE_ATTEMPTS = 3
const SESSION_RETRY_DELAYS_MS = [500, 1500]
const SESSION_RESTORE_OPTIONS: FetchOptions = { timeoutMs: 6000, retryOnNetworkError: false }

const GeneralProvider = ({ children }: { children: ReactNode }) => {

	const navigate = useNavigate()
	const location = useLocation()

	const [user, setUser] = useState<UserInterface | undefined>(undefined)
	const [userToken, setUserToken] = useState<JWT | undefined>(undefined)
	const [authStatus, setAuthStatus] = useState<AuthStatus>('unknown')
	const [isRetryingSession, setIsRetryingSession] = useState<boolean>(false)
	const [pushNotificationStatus, setPushNotificationStatus] = useState<PushNotificationStatus>('unknown')
	const [groupsOfUser, setGroupsOfUser] = useState<GroupOfUserInterface[]>([])
	const [groupsOfUserAsGroups, setGroupsOfUserAsGroups] = useState<GroupInterface[]>([])
	const [hasLoadedGroups, setHasLoadedGroups] = useState<boolean>(false)
	const [schoolOfUser, setSchoolOfUser] = useState<SchoolInterface | undefined>(undefined)
	const loadedGroupsSchoolIdRef = useRef<string | undefined>(undefined)

	// The URL is the only record of the current section, so the browser back button and a
	// reload both land on the section the user was looking at.
	const appLocation = ParseAppPath(location.pathname)
	const currentModal: ModalTypes['type'] = appLocation?.section ??
		(appLocation ? GetHomeSection(appLocation.area) : 'HomeModal')

	const getSectionPath = (section: ModalTypes['type'], itemId?: string) => {
		if (!appLocation) {
			return undefined
		}
		// A super admin keeps the last school they entered while browsing the school list.
		const schoolSK = appLocation.schoolSK ?? (appLocation.area === 'superAdmin' ? schoolOfUser?.SK : undefined)
		return GetSectionPath(appLocation.area, schoolSK, section, itemId)
	}

	const setCurrentModal = (section: ModalTypes['type'], itemId?: string) => {
		const path = getSectionPath(section, itemId)
		if (path && path !== location.pathname) {
			navigate(path)
		}
	}


	const fetchAndSetData = async (url: string, setter: (data: any) => void) => {
		try {
			const dataToSet = await FetchGetFunction(url)
			if(!dataToSet) {
				return
			}
			console.log("Data to set ", dataToSet)
			setter(dataToSet);
		} catch (error) {}
	};

	// Loading groups costs one request per group, so it is guarded against being run twice
	// for the same school. The ref is updated synchronously so overlapping calls collapse.
	const updateGroupsOfUser = async (schoolId: string, forceReload: boolean = false) => {
		if (!user || !schoolId) {
			return
		}
		if (!forceReload && loadedGroupsSchoolIdRef.current === schoolId) {
			return
		}
		loadedGroupsSchoolIdRef.current = schoolId
		try {
			const url = `${process.env.REACT_APP_BACKEND_URL}/user-groups/${user.SK}?school_id=${schoolId}`;
			const groupOfUsersFromBackend = await FetchGetFunction(url)
			setGroupsOfUser(groupOfUsersFromBackend);
			await getInfoOfGroups(groupOfUsersFromBackend, schoolId)
			setHasLoadedGroups(true)
		} catch (error) {
			// Nothing was loaded for this school, so let a later attempt try again.
			loadedGroupsSchoolIdRef.current = undefined
			setHasLoadedGroups(false)
			console.error('No pudimos cargar los grupos del usuario:', error)
		}
	};

	const getInfoOfSchool = async (schoolID : string) => {
		const schoolUrl = `${process.env.REACT_APP_BACKEND_URL}/schools/${schoolID}`;
		await fetchAndSetData(schoolUrl, setSchoolOfUser);
	}

	const getInfoOfGroups = async (groupsOfUser: GroupOfUserInterface[], schoolId: string) => {
		const currentGroupList = await Promise.all(groupsOfUser.map(async (group) => {
			const url = `${process.env.REACT_APP_BACKEND_URL}/groups/${group.group_id}?school_id=${schoolId}`;
			const groupData = await FetchGetFunction(url);
			return { ...groupData, students: groupData?.students ?? [] };
		}));
		setGroupsOfUserAsGroups(currentGroupList);
	};

	// Throws on failure on purpose: callers need to tell a broken connection apart from a
	// rejected session, and the old empty-school fallback silently redirected to the landing page.
	const getInfoOfUser = async (username: string, options?: FetchOptions):
		Promise<{admin: string, school: string, user : UserInterface}> => {
		const url = `${process.env.REACT_APP_BACKEND_URL}/user-school/${username}`;
		// The user is staring at a spinner while this runs, so it gets the short budget.
		const response = await FetchGetFunction(url, options ?? { timeoutMs: LOGIN_TIMEOUT_MS })
		const userTranslated: UserInterface = {
			name: response.name,
			position: response.position,
			email: response.email,
			SK: response.SK,
			notification_token: response.notification_token,
			off_mode_until: response.off_mode_until,
			defaults: response.defaults,
			schedule: response.schedule,
			profile_image: response.profile_image || '',
			admin: response.admin || 'No',
			school: response.PK.split("#")[0]
		};
		setUser(userTranslated);
		return { admin: response.admin || 'No', school: response.PK.split("#")[0], user: userTranslated};
	};

	const turnOffSleepMode = async () => {
		const formattedDate = GetDayFromNow(-1);
		await sendDateToBackEnd(formattedDate);
	};

	const sendDateToBackEnd = async (formattedDate: string) => {
		if (!user || !schoolOfUser) {
			return
		}
		try {
			const url = `${process.env.REACT_APP_BACKEND_URL}/users/${user.SK}`;
			const body = {
				attrs: {
					off_mode_until: formattedDate,
				},
				school_id: schoolOfUser.SK,
			};
			await FetchPutFunction(url, body)
			const newUser = { ...user, off_mode_until: formattedDate };
			setUser(newUser);
		} catch (error) {}
	};

	const loadSignedInUser = async (username: string, options?: FetchOptions) => {
		const session = await fetchAuthSession()
		const userData = await getInfoOfUser(username, options)
		setUserToken(session?.tokens?.accessToken)
		setAuthStatus('authenticated')
		// Deliberately not awaited: notification setup must never delay reaching the app.
		OneSignalInit(username).then(setPushNotificationStatus)
		return userData
	}

	const handleLogin = async (username : string) => {
		const userData = await loadSignedInUser(username)
		navigate(GetRedirectURL(userData.admin, userData.school), { replace: true })
	}

	// Rebuilds the session on a reload from the Cognito tokens themselves, so it survives
	// closing the tab. Navigation is left alone here to keep deep links working.
	const restoreSession = async () => {
		setAuthStatus('unknown')
		setIsRetryingSession(false)
		let username: string | undefined
		try {
			username = await GetSignedInUsername()
		} catch (error) {
			console.error('Could not read the stored session:', error)
			setAuthStatus('anonymous')
			return
		}
		if (!username) {
			setAuthStatus('anonymous')
			return
		}
		for (let attempt = 0; attempt < SESSION_RESTORE_ATTEMPTS; attempt++) {
			if (attempt > 0) {
				setIsRetryingSession(true)
				await Delay(SESSION_RETRY_DELAYS_MS[attempt - 1])
			}
			try {
				await loadSignedInUser(username, SESSION_RESTORE_OPTIONS)
				setIsRetryingSession(false)
				return
			} catch (error) {
				// A rejected session cannot be fixed by trying again, only a bad connection can.
				if (!IsConnectionProblem(error)) {
					console.error('The stored session is no longer usable:', error)
					setIsRetryingSession(false)
					setAuthStatus('anonymous')
					return
				}
				console.warn(`Session restore attempt ${attempt + 1} failed because of the connection`)
			}
		}
		setIsRetryingSession(false)
		setAuthStatus('error')
	}

	useEffect(() => {
		restoreSession()
		// restoreSession is rebuilt on every render, so depending on it would restart the
		// session restore in a loop. This must run once on mount and nothing else.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Keeps one account's data from lingering when someone else signs in on the same browser.
	useEffect(() => {
		if (authStatus !== 'anonymous') {
			return
		}
		setUser(undefined)
		setUserToken(undefined)
		setSchoolOfUser(undefined)
		setGroupsOfUser([])
		setGroupsOfUserAsGroups([])
		setHasLoadedGroups(false)
		loadedGroupsSchoolIdRef.current = undefined
		setPushNotificationStatus('unknown')
	}, [authStatus])

	useEffect(() => {
		if (!schoolOfUser) {
			return
		}
		updateGroupsOfUser(schoolOfUser.SK)
		// updateGroupsOfUser is rebuilt on every render, so depending on it would refetch
		// every group of the school on every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [schoolOfUser])
	
	return (
		<GeneralContext.Provider value={{
			currentModal, setCurrentModal, getSectionPath, appLocation,
			user, setUser, userToken, setUserToken, authStatus, setAuthStatus, isRetryingSession, restoreSession, pushNotificationStatus, groupsOfUser,
			setGroupsOfUser, groupsOfUserAsGroups, setGroupsOfUserAsGroups, hasLoadedGroups, setHasLoadedGroups, 
			schoolOfUser, setSchoolOfUser, updateGroupsOfUser, turnOffSleepMode, sendDateToBackEnd, getInfoOfUser, 
			getInfoOfSchool, handleLogin
		}}>
			{children}
		</GeneralContext.Provider>
	)
}

function useGeneralContext() {
	const context = useContext(GeneralContext);
	if (!context) {
		throw new Error('useModal must be used within a ModalProvider');
	}
	return context;
}

export { useGeneralContext }
export default GeneralProvider;